import { toConsumableArray as _toConsumableArray, slicedToArray as _slicedToArray } from '../_virtual/_rollupPluginBabelHelpers.js';
import { useMemo, useRef, useState, useCallback, useEffect } from 'react';
import { useKeyPress, useIsDoubleClick, utils } from '@costar/theme-ui';
import { useSelectableList } from '@costar/theme-ui-select';
import { useTransferListContext } from './transfer-list-context.esm.js';

// import { useTransferListContext } from './transfer-list-context'
// import { useWhatChanged } from '@simbathesailor/use-what-changed'

var emptyArray = [];
var emptyObject = {};
var emptyAvailableSelectableListOptions = {
  name: 'transfer-list-available-options',
  optionList: [],
  noFuzzySearch: false
};
function not(a, b) {
  return a.filter(function (value) {
    return b.indexOf(value) === -1;
  });
}
function intersection(a, b) {
  return a.filter(function (value) {
    return b.indexOf(value) !== -1;
  });
}
function shouldShiftPositions(acc, cur, nextIndex, curState, selectedChecked) {
  return nextIndex <= curState.length - 1 && !selectedChecked.includes(cur) && selectedChecked.includes(curState[nextIndex]) && !acc.includes(curState[nextIndex]);
}
function getNextUncheckedIndex(indx, curState, selectedChecked) {
  var retVal = indx;
  while (selectedChecked.includes(curState[retVal])) {
    retVal++;
  }
  return retVal;
}
var shiftPositions = function shiftPositions(curState, selectedChecked) {
  return function (acc, cur, indx) {
    if (acc.includes(cur)) {
      return acc;
    }
    var nextIndex = indx + 1;
    var shouldMove = shouldShiftPositions(acc, cur, nextIndex, curState, selectedChecked);
    var nextCheckedIndex = shouldMove ? getNextUncheckedIndex(nextIndex, curState, selectedChecked) : indx;
    return [].concat(_toConsumableArray(acc), _toConsumableArray(shouldMove ? [].concat(_toConsumableArray(nextCheckedIndex > nextIndex ? Array.from(Array(nextCheckedIndex - nextIndex).keys()).reduce(function (_acc, _cur) {
      return acc.includes(curState[indx + _cur + 1]) ? _acc : [].concat(_toConsumableArray(_acc), [curState[indx + _cur + 1]]);
    }, []) : [curState[nextIndex]]), _toConsumableArray(acc.includes(cur) ? [] : [cur])) : [cur]));
  };
};
var initialCheckedState = {
  source: 'empty',
  checked: []
};
function addRange(lastIndex, value, sourceList, targetList) {
  var currentIndex = sourceList.indexOf(value);
  currentIndex < lastIndex ? targetList.push.apply(targetList, _toConsumableArray(sourceList.slice(currentIndex, lastIndex))) : targetList.push.apply(targetList, _toConsumableArray(sourceList.slice(lastIndex + 1, currentIndex + 1)));
}
var useTransferListState = (function (props) {
  // eslint-disable-next-line no-console
  // console.time('----- use-transfer-list-state -----')
  var _props$availableOptio = props.availableOptions,
    availableOptions = _props$availableOptio === void 0 ? emptyObject : _props$availableOptio,
    _props$hiddenOptions = props.hiddenOptions,
    hiddenOptions = _props$hiddenOptions === void 0 ? emptyArray : _props$hiddenOptions,
    _props$sortMode = props.sortMode,
    sortMode = _props$sortMode === void 0 ? 'optionOrder' : _props$sortMode,
    isAvailableOptionsSearchable = props.isAvailableOptionsSearchable,
    initialList = props.initialList,
    setInitialState = props.setInitialState,
    isControlled = props.isControlled;
  var lockedSelections = useMemo(function () {
    return Object.keys(availableOptions).filter(function (key) {
      var _availableOptions$key;
      return !!((_availableOptions$key = availableOptions[key]) !== null && _availableOptions$key !== void 0 && _availableOptions$key.lock);
    });
  }, [availableOptions]);
  var selectedList = useMemo(function () {
    return [].concat(_toConsumableArray(lockedSelections), _toConsumableArray(initialList.filter(function (s) {
      return !lockedSelections.includes(s);
    })));
  }, [initialList, lockedSelections]);
  var previousSelectionRef = useRef(initialList);
  var _ref = useTransferListContext() || {},
    revertChangeEventObserver = _ref.revertChangeEventObserver;
  var lastSelectIndexRef = useRef(-1);
  var _useState = useState(initialCheckedState),
    _useState2 = _slicedToArray(_useState, 2),
    checkedState = _useState2[0],
    setCheckedState = _useState2[1];
  var _useState3 = useState([]),
    _useState4 = _slicedToArray(_useState3, 2),
    availableList = _useState4[0],
    setAvailableList = _useState4[1];
  var setSelectedList = useCallback(function (value) {
    setInitialState(function (prevState) {
      previousSelectionRef.current = prevState;
      return typeof value === 'function' ? value(prevState) : value;
    });
  }, [setInitialState]);
  useEffect(function () {
    if (!isControlled) {
      var handleRevertChange = function handleRevertChange() {
        setInitialState(previousSelectionRef.current);
      };
      revertChangeEventObserver === null || revertChangeEventObserver === void 0 ? void 0 : revertChangeEventObserver.attach(handleRevertChange);
      return function () {
        return revertChangeEventObserver === null || revertChangeEventObserver === void 0 ? void 0 : revertChangeEventObserver.detach(handleRevertChange);
      };
    }
  }, [isControlled, revertChangeEventObserver, setInitialState]);
  var _useKeyPress = useKeyPress('control'),
    _useKeyPress2 = _slicedToArray(_useKeyPress, 2);
    _useKeyPress2[0];
    var ctrlKeyPressedRef = _useKeyPress2[1];
  var _useKeyPress3 = useKeyPress('shift'),
    _useKeyPress4 = _slicedToArray(_useKeyPress3, 2);
    _useKeyPress4[0];
    var shiftKeyPressedRef = _useKeyPress4[1];
  var isDoubleClickFunc = useIsDoubleClick();
  var availableSelectableListOptions = useMemo(function () {
    return !isAvailableOptionsSearchable ? emptyAvailableSelectableListOptions : {
      name: 'transfer-list-available-options',
      noFuzzySearch: props.noFuzzySearch,
      optionList: [{
        name: 'Available Fields',
        key: 'options.available',
        items: Object.keys(availableOptions).filter(function (key) {
          return !selectedList.includes(key) && !hiddenOptions.includes(key);
        }).map(function (key) {
          var _availableOptions$key2, _availableOptions$key3;
          return {
            key: key,
            name: utils.reactNodeToString(((_availableOptions$key2 = availableOptions[key]) === null || _availableOptions$key2 === void 0 ? void 0 : _availableOptions$key2.longLabel) || ((_availableOptions$key3 = availableOptions[key]) === null || _availableOptions$key3 === void 0 ? void 0 : _availableOptions$key3.label) || key)
          };
        })
      }]
    };
  }, [isAvailableOptionsSearchable, availableOptions, selectedList, hiddenOptions, props.noFuzzySearch]);
  var _useSelectableList = useSelectableList(availableSelectableListOptions),
    getActiveListSearchProps = _useSelectableList.getCategorySelectProps;
  var activeListSearchProps = useMemo(function () {
    return getActiveListSearchProps();
  }, [getActiveListSearchProps]);
  var availableSortStrategy = useMemo(function () {
    if (sortMode === 'optionOrder') {
      return function (a, b) {
        var _availableOptions$a, _availableOptions$b, _availableOptions$a2, _availableOptions$b2, _availableOptions$a3, _availableOptions$b3;
        if ((_availableOptions$a = availableOptions[a]) !== null && _availableOptions$a !== void 0 && _availableOptions$a.lock && !((_availableOptions$b = availableOptions[b]) !== null && _availableOptions$b !== void 0 && _availableOptions$b.lock)) {
          return -1;
        }
        if (!((_availableOptions$a2 = availableOptions[a]) !== null && _availableOptions$a2 !== void 0 && _availableOptions$a2.lock) && (_availableOptions$b2 = availableOptions[b]) !== null && _availableOptions$b2 !== void 0 && _availableOptions$b2.lock) {
          return 1;
        }
        return ((_availableOptions$a3 = availableOptions[a]) === null || _availableOptions$a3 === void 0 ? void 0 : _availableOptions$a3.order) - ((_availableOptions$b3 = availableOptions[b]) === null || _availableOptions$b3 === void 0 ? void 0 : _availableOptions$b3.order);
      };
    } else if (sortMode === 'alphabetical') {
      return function (a, b) {
        var _availableOptions$a4, _availableOptions$b4, _availableOptions$a5, _availableOptions$b5, _availableOptions$a6, _availableOptions$b6;
        if ((_availableOptions$a4 = availableOptions[a]) !== null && _availableOptions$a4 !== void 0 && _availableOptions$a4.lock && !((_availableOptions$b4 = availableOptions[b]) !== null && _availableOptions$b4 !== void 0 && _availableOptions$b4.lock)) {
          return -1;
        }
        if (!((_availableOptions$a5 = availableOptions[a]) !== null && _availableOptions$a5 !== void 0 && _availableOptions$a5.lock) && (_availableOptions$b5 = availableOptions[b]) !== null && _availableOptions$b5 !== void 0 && _availableOptions$b5.lock) {
          return 1;
        }
        var aString = utils.reactNodeToString((_availableOptions$a6 = availableOptions[a]) === null || _availableOptions$a6 === void 0 ? void 0 : _availableOptions$a6.label);
        var bString = utils.reactNodeToString((_availableOptions$b6 = availableOptions[b]) === null || _availableOptions$b6 === void 0 ? void 0 : _availableOptions$b6.label);
        return aString.toLowerCase() < bString.toLowerCase() ? -1 : aString.toLowerCase() > bString.toLowerCase() ? 1 : 0;
      };
    } else {
      return function (a, b) {
        return sortMode(availableOptions[a], availableOptions[b]);
      };
    }
  }, [sortMode, availableOptions]);

  // useWhatChanged(
  //   [
  //     availableOptions,
  //     hiddenOptions,
  //     selectedList,
  //     setAvailableList,
  //     availableSortStrategy,
  //     activeListSearchProps.highlightedStrings,
  //     activeListSearchProps.search,
  //     lockedSelections
  //   ],
  //   'availableOptions, hiddenOptions, selectedList, setAvailableList, availableSortStrategy,  activeListSearchProps.highlightedStrings, activeListSearchProps.search, lockedSelections'
  // )

  useEffect(function () {
    setAvailableList(Object.keys(availableOptions).filter(function (i) {
      return !selectedList.includes(i) && !hiddenOptions.includes(i) && (!activeListSearchProps.search || activeListSearchProps.highlightedStrings[i]);
    }).sort(availableSortStrategy));
  }, [availableOptions, hiddenOptions, selectedList, setAvailableList, availableSortStrategy, activeListSearchProps.highlightedStrings, activeListSearchProps.search, lockedSelections]);
  useEffect(function () {
    if (!activeListSearchProps.search) {
      return;
    }
    setCheckedState(function (state) {
      var availableChecked = intersection(state.checked, Object.keys(activeListSearchProps.highlightedStrings));
      return availableChecked.length == state.checked.length ? state : {
        source: 'availableList',
        checked: availableChecked
      };
    });
  }, [activeListSearchProps.search, activeListSearchProps.highlightedStrings]);
  var availableChecked = useMemo(function () {
    return intersection(checkedState.checked, availableList);
  }, [availableList, checkedState.checked]);
  var selectedChecked = useMemo(function () {
    return intersection(checkedState.checked, selectedList);
  }, [checkedState.checked, selectedList]);

  // useWhatChanged(
  //   [
  //     isDoubleClickFunc,
  //     availableList,
  //     ctrlKeyPressedRef,
  //     shiftKeyPressedRef,
  //     selectedList,
  //     availableSortStrategy
  //   ],
  //   'isDoubleClickFunc, availableList, ctrlKeyPressedRef, shiftKeyPressedRef, selectedList, availableSortStrategy'
  // )

  var handleToggle = useCallback(function (value) {
    return function () {
      // `_isDoubleClickEvent` was not reliable: `_isDoubleClickEvent` was triggered for 2 items in the list rather than one.`
      // const isDoubleClick = isDoubleClickEvent || isDoubleClickFunc(value) // This was causing two items to be selected on a double click. Bug #1911242.
      var isDoubleClick = isDoubleClickFunc(value);
      setCheckedState(function (checkedState) {
        var currentIndex = checkedState.checked.indexOf(value);
        var isFromAvailableList = availableList.indexOf(value) > -1;
        var canAddToCheckedList = isFromAvailableList && checkedState.source !== 'selectedList' || !isFromAvailableList && checkedState.source !== 'availableList';
        var newChecked = _toConsumableArray(canAddToCheckedList && (ctrlKeyPressedRef.current || shiftKeyPressedRef.current) ? checkedState.checked : []);
        if (newChecked.length === 0) {
          newChecked.push(value);
        } else if (currentIndex === -1) {
          // add new
          if (shiftKeyPressedRef.current) {
            if (isFromAvailableList) {
              addRange(lastSelectIndexRef.current, value, availableList, newChecked);
            } else {
              addRange(lastSelectIndexRef.current, value, selectedList, newChecked);
            }
          } else {
            newChecked.push(value);
          }
        } else {
          // remove
          newChecked.splice(currentIndex, 1);
        }
        lastSelectIndexRef.current = isFromAvailableList ? availableList.indexOf(value) : selectedList.indexOf(value);
        return {
          source: isFromAvailableList ? 'availableList' : 'selectedList',
          checked: newChecked
        };
      });
      if (isDoubleClick) {
        if (selectedList.includes(value)) {
          setSelectedList(not(selectedList, [value]));
        }
        if (availableList.includes(value)) {
          setSelectedList([].concat(_toConsumableArray(selectedList), [value]));
        }
      }
    };
  }, [isDoubleClickFunc, availableList, ctrlKeyPressedRef, shiftKeyPressedRef, selectedList, setSelectedList]);
  var moveAllAvailableToSelectedList = useCallback(function () {
    setSelectedList(function (state) {
      return state.concat(availableList);
    });
  }, [availableList, setSelectedList]);
  var moveCheckedAvailableToSelectedList = useCallback(function () {
    setSelectedList(function (state) {
      return state.concat(availableChecked);
    });
  }, [availableChecked, setSelectedList]);
  var moverCheckedSelectedToAvailableList = useCallback(function () {
    setSelectedList(function (state) {
      return not(state, selectedChecked);
    });
  }, [selectedChecked, setSelectedList]);
  var moveAllSelectedToAvailableList = useCallback(function () {
    setSelectedList(function (state) {
      return _toConsumableArray(state.filter(function (i) {
        var _availableOptions$i;
        return (_availableOptions$i = availableOptions[i]) === null || _availableOptions$i === void 0 ? void 0 : _availableOptions$i.lock;
      }));
    });
  }, [availableOptions, setSelectedList]);
  var moveAllSelectedCheckToFirst = useCallback(function () {
    setSelectedList(function (state) {
      return [].concat(_toConsumableArray(state.filter(function (i) {
        var _availableOptions$i2;
        return (_availableOptions$i2 = availableOptions[i]) === null || _availableOptions$i2 === void 0 ? void 0 : _availableOptions$i2.lock;
      })), _toConsumableArray(selectedChecked.filter(function (i) {
        var _availableOptions$i3;
        return !((_availableOptions$i3 = availableOptions[i]) !== null && _availableOptions$i3 !== void 0 && _availableOptions$i3.lock);
      })), _toConsumableArray(state.filter(function (i) {
        return !selectedChecked.includes(i);
      }).filter(function (i) {
        var _availableOptions$i4;
        return !((_availableOptions$i4 = availableOptions[i]) !== null && _availableOptions$i4 !== void 0 && _availableOptions$i4.lock);
      })));
    });
  }, [availableOptions, selectedChecked, setSelectedList]);
  var moveSelectedCheckPositionForward = useCallback(function () {
    setSelectedList(function (state) {
      var result = state.reduce(shiftPositions(state, selectedChecked), []);
      return [].concat(_toConsumableArray(state.filter(function (i) {
        var _availableOptions$i5;
        return (_availableOptions$i5 = availableOptions[i]) === null || _availableOptions$i5 === void 0 ? void 0 : _availableOptions$i5.lock;
      })), _toConsumableArray(result.filter(function (i) {
        var _availableOptions$i6;
        return !((_availableOptions$i6 = availableOptions[i]) !== null && _availableOptions$i6 !== void 0 && _availableOptions$i6.lock);
      })));
    });
  }, [availableOptions, selectedChecked, setSelectedList]);
  var moveSelectedCheckPositionBack = useCallback(function () {
    setSelectedList(function (state) {
      var reverseList = state.slice().reverse();
      return reverseList.reduce(shiftPositions(reverseList, selectedChecked), []).reverse();
    });
  }, [selectedChecked, setSelectedList]);
  var moveAllSelectedCheckToLast = useCallback(function () {
    setSelectedList(function (state) {
      return [].concat(_toConsumableArray(state.filter(function (i) {
        return !selectedChecked.includes(i);
      })), _toConsumableArray(selectedChecked));
    });
  }, [selectedChecked, setSelectedList]);
  // eslint-disable-next-line no-console
  // console.timeEnd('----- use-transfer-list-state -----')
  return {
    selectedList: selectedList,
    availableList: availableList,
    checked: checkedState.checked,
    availableChecked: availableChecked,
    selectedChecked: selectedChecked,
    firstChecked: selectedList[Math.min.apply(Math, _toConsumableArray(checkedState.checked.map(function (i) {
      return selectedList.indexOf(i);
    })))],
    lastChecked: selectedList[Math.max.apply(Math, _toConsumableArray(checkedState.checked.map(function (i) {
      return selectedList.indexOf(i);
    })))],
    lockedSelections: lockedSelections,
    handleToggle: handleToggle,
    moveAllAvailableToSelectedList: moveAllAvailableToSelectedList,
    moveCheckedAvailableToSelectedList: moveCheckedAvailableToSelectedList,
    moverCheckedSelectedToAvailableList: moverCheckedSelectedToAvailableList,
    moveAllSelectedToAvailableList: moveAllSelectedToAvailableList,
    moveAllSelectedCheckToFirst: moveAllSelectedCheckToFirst,
    moveSelectedCheckPositionForward: moveSelectedCheckPositionForward,
    moveSelectedCheckPositionBack: moveSelectedCheckPositionBack,
    moveAllSelectedCheckToLast: moveAllSelectedCheckToLast,
    activeListSearchProps: activeListSearchProps,
    defaultSelectedOptions: selectedList
  };
});

export default useTransferListState;
//# sourceMappingURL=use-transfer-list-state.esm.js.map
